// File: e1c/src/components/content_editor/MediaAttachmentManager.js
// REVISED: Fixes asset ID bug and implements a more compact UI to reduce clutter.

import React, { useState, useMemo } from 'react';
import {
    Box, Grid, Paper, IconButton, Button, Typography, List, ListItem,
    ListItemAvatar, Avatar, ListItemText, Tooltip
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import ImageIcon from '@mui/icons-material/Image';
import VideocamIcon from '@mui/icons-material/Videocam';
import MediaLibraryModal from './MediaLibraryModal';

const MAX_MEDIA_ITEMS = 4;

const MediaAttachmentManager = ({ mediaAssets = [], onChange }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const attachedMedia = useMemo(() => (Array.isArray(mediaAssets) ? mediaAssets : []), [mediaAssets]);
    
    // FIX: Get the API base URL to prepend to relative thumbnail paths
    const apiUrlBase = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleRemoveAsset = (idToRemove) => {
        // FIX: Use asset.id to correctly filter the assets
        const newAssets = attachedMedia.filter(asset => asset.id !== idToRemove);
        onChange({ media_assets: newAssets });
    };

    const handleSelectMedia = (selectedAssets) => {
        // FIX: Use asset.id as the key to avoid duplicates and ensure correctness
        const newAssetMap = new Map(attachedMedia.map(asset => [asset.id, asset]));
        
        for (const newAsset of selectedAssets) {
            if (newAssetMap.size < MAX_MEDIA_ITEMS) {
                newAssetMap.set(newAsset.id, newAsset);
            } else {
                console.warn(`Media limit (${MAX_MEDIA_ITEMS}) reached. Some selections were not added.`);
                break;
            }
        }
        
        onChange({ media_assets: Array.from(newAssetMap.values()) });
        handleCloseModal();
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Media Attachments (Optional, Max {MAX_MEDIA_ITEMS})
            </Typography>
            <Paper variant="outlined" sx={{ p: 1, backgroundColor: '#fdfdfd' }}>
                {/* UI IMPROVEMENT: Use a List for a more compact view */}
                <List dense>
                    {attachedMedia.map((asset) => (
                        <ListItem
                            // FIX: Use asset.id for the key
                            key={asset.id}
                            secondaryAction={
                                <Tooltip title="Remove this asset">
                                    <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveAsset(asset.id)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                            }
                        >
                            <ListItemAvatar>

                                {/* FIX: Prepend apiUrlBase to relative thumbnail URLs */}
                                <Avatar variant="rounded" 
                                    src={asset.thumbnail_url?.startsWith('http') ? asset.thumbnail_url : `${apiUrlBase}${asset.thumbnail_url}`}
                                >
                                    {asset.media_type === 'video' ? <VideocamIcon /> : <ImageIcon />}
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={asset.original_filename || 'Attached Media'}
                                primaryTypographyProps={{ variant: 'body2', noWrap: true, textOverflow: 'ellipsis' }}
                            />
                        </ListItem>
                    ))}
                </List>

                {attachedMedia.length < MAX_MEDIA_ITEMS && (
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={handleOpenModal}
                        startIcon={<AddCircleOutlineIcon />}
                        sx={{ mt: attachedMedia.length > 0 ? 1 : 0, borderStyle: 'dashed' }}
                    >
                        Add Media from Library
                    </Button>
                )}
            </Paper>

            <MediaLibraryModal
                open={isModalOpen}
                onClose={handleCloseModal}
                onSelectMedia={handleSelectMedia}
            />
        </Box>
    );
};

export default MediaAttachmentManager;